import { useState } from 'react';
import { useDeepSignal } from '../context';
import { buildModelRowsFromApi, buildModelStrategicReads } from '../builders';
import { SectionLabel, SectionTitle, DSLoadingRow, DSEmptyState } from '../components/section-primitives';

export function ModelBreakdownSection() {
  const { apiData, loading } = useDeepSignal();
  const [expandedModel, setExpandedModel] = useState<string | null>(null);

  if (loading) return (
    <div style={{ padding: '32px 64px' }}>
      {[1,2,3,4].map(i => <DSLoadingRow key={i} />)}
    </div>
  );
  if (!apiData) return <DSEmptyState message="No model breakdown data available." />;

  const mRows = buildModelRowsFromApi(apiData);
  const strategicReads = buildModelStrategicReads(apiData);

  return (
    <div style={{ padding: '32px 64px' }}>
      <div style={{ marginBottom: '24px' }}>
        <SectionLabel>Model Breakdown</SectionLabel>
        <SectionTitle size="xl">How each model converts mentions into recommendations.</SectionTitle>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', marginTop: '4px', lineHeight: '1.5' }}>
          How each AI model performs across the recommendation funnel and how efficiently mentions convert to primary recommendations.
        </p>
      </div>

      {/* Table header */}
      <div style={{ height: '1px', backgroundColor: 'var(--border)' }} />
      <div style={{
        display: 'grid',
        gridTemplateColumns: '180px 110px 110px 140px 110px 1fr',
        gap: '12px',
        padding: '14px 0',
        borderBottom: '1px solid var(--border)',
      }}>
        {['Model', 'Mention', 'Top-5', 'Primary Rec', 'Conv %', ''].map((h, i) => (
          <div key={i} style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--muted-foreground)', letterSpacing: '0.2px' }}>{h}</div>
        ))}
      </div>

      {/* Rows */}
      {mRows.filter((row): row is NonNullable<typeof row> => row !== null).map((row) => (
        <div key={row.name} style={{
          display: 'grid',
          gridTemplateColumns: '180px 110px 110px 140px 110px 1fr',
          gap: '12px',
          borderBottom: '1px solid var(--border)',
          alignItems: 'center',
          minHeight: '88px',
          padding: '8px 0',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '11px', height: '11px', borderRadius: '50%', backgroundColor: row.dotColor, flexShrink: 0 }} />
            <span style={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--foreground)' }}>{row.name}</span>
          </div>
          <span style={{ fontSize: 'var(--text-base)', color: 'var(--foreground)' }}>{row.mention}</span>
          <span style={{ fontSize: 'var(--text-base)', color: 'var(--foreground)' }}>{row.top5}</span>
          <span style={{ fontSize: 'var(--text-base)', fontWeight: 700, color: '#28a9d2' }}>{row.primaryRec}</span>
          <span style={{ fontSize: 'var(--text-base)', fontWeight: 700, color: '#00c9c4' }}>{row.conv}</span>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', lineHeight: '1.55' }}>{row.note}</p>
        </div>
      ))}

      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', marginTop: '18px' }}>
        Conv % = Primary Rec divided by Mention. How efficiently each model converts brand awareness into recommendations.
      </p>

      {/* Strategic reads */}
      <div style={{ marginTop: '40px' }}>
        <div style={{ marginBottom: '20px' }}>
          <SectionLabel>Model Analysis</SectionLabel>
          <SectionTitle size="xl">How each model behaves in this dataset.</SectionTitle>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {strategicReads.map((read) => {
            const isExpanded = expandedModel === read.name;
            return (
              <div key={read.name} style={{ border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden', backgroundColor: 'var(--background)' }}>
                <button
                  onClick={() => setExpandedModel(isExpanded ? null : read.name)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px 20px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    borderBottom: isExpanded ? '1px solid var(--border)' : 'none',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', flex: 1, minWidth: 0, marginRight: '16px' }}>
                    <div style={{ width: '4px', height: '18px', borderRadius: '2px', backgroundColor: read.dotColor, flexShrink: 0, marginTop: '1px' }} />
                    <div style={{ minWidth: 0 }}>
                      <span style={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--foreground)', display: 'block', marginBottom: '3px' }}>{read.name}</span>
                      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', lineHeight: '1.5', display: 'block' }}>{read.snippet}</span>
                    </div>
                  </div>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ color: 'var(--muted-foreground)', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', flexShrink: 0 }}>
                    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>

                {isExpanded && (
                  <div style={{ padding: '20px 24px 24px 38px' }}>
                    <p style={{ fontSize: 'var(--text-sm)', color: 'var(--foreground)', lineHeight: '1.7', margin: 0 }}>
                      {read.strategic}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
